/*
 * brandedEmailTemplate.js — Iter 186.2 (Feb 2026)
 *
 * Wraps plain-body HTML in an Enable Healthcare-branded outer frame so
 * quote-attached emails match the PDF letterhead visually. All styles
 * are inline (no <style> block, no external CSS) so every major email
 * client renders it identically. The blue emblem is the same LOGO_BLUE
 * base64 sourced from /public/_mockup_ref.html, keeping the print PDF
 * and the delivery email pixel-consistent.
 *
 * Usage:
 *   buildBrandedEmailHtml(bodyHtml, {
 *     quoteNumber: "QT-1042",
 *     preparedForOrg: "Northside Family Practice",
 *     issuedAt: new Date(),
 *   })
 *
 * Output is a complete <table>-based document — no <html> / <body>
 * needed because M365 wraps the payload as an HTML message anyway.
 */
import { LOGO_BLUE } from "@/assets/enableHealthcareLogos";

const fmtDate = (d) => {
  try {
    const x = d instanceof Date ? d : new Date(d);
    return x.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

/**
 * Wraps `bodyHtml` in the Enable Healthcare branded envelope.
 * All styles are inline for maximum email-client compatibility.
 */
export function buildBrandedEmailHtml(bodyHtml, opts = {}) {
  const {
    quoteNumber,
    preparedForOrg,
    issuedAt,
    footerText = "Enable Healthcare Inc. · 225 Brae Blvd, Suite 125 Park Ridge, NJ 07656 · 866 876 8272",
    accentColor = "#0e2a55",
  } = opts;

  const meta = [];
  if (quoteNumber) meta.push(`Quote <strong>#${quoteNumber}</strong>`);
  if (issuedAt) meta.push(`Issued ${fmtDate(issuedAt)}`);
  if (preparedForOrg) meta.push(`Prepared for <strong>${escapeHtml(preparedForOrg)}</strong>`);

  return `
<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background:#f5f7fb;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <tr>
    <td align="center" style="padding:24px 12px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;background:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 4px 14px rgba(15,23,42,0.10);">
        <!-- Letterhead -->
        <tr>
          <td style="background:#ffffff;padding:22px 32px 14px;border-bottom:3px solid ${accentColor};">
            <img src="${LOGO_BLUE}" alt="Enable Healthcare" width="180" style="display:block;height:auto;max-width:180px;" />
          </td>
        </tr>
        <!-- Meta strip -->
        ${meta.length ? `
        <tr>
          <td style="padding:12px 32px 0;font-size:11px;color:#6b7280;letter-spacing:0.03em;text-transform:uppercase;font-weight:600;">
            ${meta.join(' · ')}
          </td>
        </tr>` : ``}
        <!-- Body -->
        <tr>
          <td style="padding:20px 32px 24px;font-size:14px;line-height:1.6;color:#1f2937;">
            ${bodyHtml || ''}
          </td>
        </tr>
        <!-- Footer -->
        <tr>
          <td style="padding:14px 32px 22px;border-top:1px solid #e5e7eb;font-size:10px;color:#6b7280;line-height:1.55;">
            ${escapeHtml(footerText)}
            <br />
            <a href="https://www.ehiehr.com" style="color:#2563eb;text-decoration:none;">www.ehiehr.com</a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`.trim();
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export default buildBrandedEmailHtml;
